<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { AgentCard, AgentDebugToolStep, AgentMapContext } from "@/services/api/agentApi";
import type { StreamedAgentStep } from "@/services/api/agentV2Api";
import type { AgentGuidance, AgentGuidanceSuggestion, AgentV2ChatResponse, CommandProgressItem, V4ProcessStep } from "@/agent/types";
import type { TurnFinalAnswer } from "@/agent/TurnFinalAnswer";
import {
  playBase64Audio,
  recognizeSpeech,
  synthesizeSpeech
} from "@/services/api/voiceApi";
import type { TtsResponse } from "@/services/api/voiceApi";

const props = defineProps<{
  response: AgentV2ChatResponse | null;
  loading: boolean;
  errorMessage: string;
  commandProgress?: CommandProgressItem[] | null;
  finalAnswer?: TurnFinalAnswer | null;
  /** 命令队列执行中（页面操作尚未全部完成） */
  executing?: boolean;
  /** 等待回复期间流式到达的执行步骤（最终结果到达前逐条追加） */
  liveSteps?: StreamedAgentStep[] | null;
  /** V4 自己的图阶段进度，不使用 V3 debugToolSteps。 */
  v4ProcessSteps?: V4ProcessStep[] | null;
  /** V4 请求尚未完成时先展示的简短接话。 */
  interimAnswer?: string | null;
  /** 空会话 welcome state 使用同一份页面 world state，不请求模型。 */
  welcomeContext?: AgentMapContext | null;
}>();

const emit = defineEmits<{
  submit: [message: string];
  cancel: [];
  reset: [];
}>();

interface AgentTurn {
  id: number;
  question: string;
  answer: string;
  cards: AgentCard[];
  toolSteps: AgentDebugToolStep[];
  commandProgress: CommandProgressItem[];
  v4ProcessSteps: V4ProcessStep[];
  backendTurnId?: string;
  suggestedActions: string[];
  guidance?: AgentGuidance | null;
  /** 正式回答已替换 */
  finalized?: boolean;
  /** 执行过程展开态（默认收起，用户可点开） */
  detailsOpen?: boolean;
  actionNotice?: string;
  copied?: boolean;
}

const POSITION_KEY = "jiangcheng-agent-position-v2";
const SPEECH_KEY = "jiangcheng-agent-speech-enabled-v1";
const ORB_SIZE = 68;
const EDGE_MARGIN = 18;

interface AgentPosition {
  x: number;
  y: number;
}

const open = ref(false);
const panelOpen = ref(false);
const text = ref("");
const barTextareaRef = ref<HTMLTextAreaElement | null>(null);
const recording = ref(false);
const voiceBusy = ref(false);
const voiceNotice = ref("");
const liveTranscript = ref("");
const speechEnabled = ref(true);
const speakingTurnId = ref<number | null>(null);
const turns = ref<AgentTurn[]>([]);
const bodyRef = ref<HTMLElement | null>(null);
const bottomRef = ref<HTMLElement | null>(null);
const position = ref<AgentPosition>({ x: 0, y: 0 });
const dragging = ref(false);
const dragMoved = ref(false);

let mediaRecorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let stream: MediaStream | null = null;
let liveRecognition: any = null;
let liveRecognitionSupportedThisTurn = false;
let currentAudio: HTMLAudioElement | null = null;
let currentSpeechResolve: (() => void) | null = null;
let speechQueue: Promise<void> = Promise.resolve();
let speechQueueToken = 0;
const turnNoticeTimers = new Map<number, number>();
let dragPointerId = 0;
let dragStart = { pointerX: 0, pointerY: 0, x: 0, y: 0 };
let turnIdSeed = 0;
let lastRenderedResponse: AgentV2ChatResponse | null = null;

const preferredAudioType =
  typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")
    ? "audio/ogg;codecs=opus"
    : "";

const latestSuggestionTurnId = computed(() => {
  const latest = [...turns.value]
    .reverse()
    .find((turn) => turn.answer && turn.suggestedActions.length);
  return latest?.id ?? 0;
});
const busy = computed(() => props.loading || props.executing);
const welcomeGuidance = computed(() => buildWelcomeGuidance(props.welcomeContext || null));

const orbStyle = computed(() => ({
  transform: `translate3d(${position.value.x}px, ${position.value.y}px, 0)`
}));

function toggleDetails(turn: AgentTurn): void {
  turn.detailsOpen = !turn.detailsOpen;
}

// ---- 悬浮球拖拽 ----

function startDrag(event: PointerEvent): void {
  if (open.value) return;
  dragPointerId = event.pointerId;
  dragMoved.value = false;
  dragging.value = true;
  dragStart = {
    pointerX: event.clientX,
    pointerY: event.clientY,
    x: position.value.x,
    y: position.value.y
  };
  (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  window.addEventListener("pointermove", moveDrag);
  window.addEventListener("pointerup", endDrag, { once: true });
}

function moveDrag(event: PointerEvent): void {
  if (!dragging.value || event.pointerId !== dragPointerId) return;
  const dx = event.clientX - dragStart.pointerX;
  const dy = event.clientY - dragStart.pointerY;
  if (Math.abs(dx) + Math.abs(dy) > 5) dragMoved.value = true;
  position.value = clampPosition({ x: dragStart.x + dx, y: dragStart.y + dy });
}

function endDrag(): void {
  if (!dragging.value) return;
  dragging.value = false;
  window.removeEventListener("pointermove", moveDrag);
  position.value = snapToEdge(position.value);
  savePosition();
  window.setTimeout(() => {
    dragMoved.value = false;
  }, 0);
}

function handleOrbClick(): void {
  if (dragMoved.value) return;
  openPanel();
}

function openPanel(): void {
  open.value = true;
  panelOpen.value = true;
}

/** 供首次引导页等外部入口复用真实的江小忆面板与对话流程。 */
function openFromExternal(message = ""): void {
  const prompt = message.trim();
  if (prompt) {
    submitSuggested(prompt);
    return;
  }
  openPanel();
}

function closePanel(): void {
  panelOpen.value = false;
}

function closeDock(): void {
  panelOpen.value = false;
  open.value = false;
}

function startNewConversation(): void {
  stopCurrentAudio();
  turns.value = [];
  text.value = "";
  lastRenderedResponse = null;
  emit("reset");
}

function clampPosition(candidate: AgentPosition): AgentPosition {
  if (typeof window === "undefined") return candidate;
  return {
    x: Math.min(window.innerWidth - ORB_SIZE - EDGE_MARGIN, Math.max(EDGE_MARGIN, candidate.x)),
    y: Math.min(window.innerHeight - ORB_SIZE - EDGE_MARGIN, Math.max(EDGE_MARGIN, candidate.y))
  };
}

function snapToEdge(candidate: AgentPosition): AgentPosition {
  if (typeof window === "undefined") return candidate;
  const rightX = window.innerWidth - ORB_SIZE - EDGE_MARGIN;
  const leftX = EDGE_MARGIN;
  return {
    ...candidate,
    x: candidate.x + ORB_SIZE / 2 < window.innerWidth / 2 ? leftX : rightX
  };
}

function initializePosition(): void {
  if (typeof window === "undefined") return;
  try {
    const saved = JSON.parse(localStorage.getItem(POSITION_KEY) || "null") as AgentPosition | null;
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
      position.value = clampPosition(saved);
      return;
    }
  } catch {
    // Ignore broken local position state.
  }
  position.value = snapToEdge({
    x: window.innerWidth - ORB_SIZE - EDGE_MARGIN,
    y: Math.round(window.innerHeight * 0.58)
  });
}

function savePosition(): void {
  localStorage.setItem(POSITION_KEY, JSON.stringify(position.value));
}

function handleResize(): void {
  position.value = snapToEdge(clampPosition(position.value));
  savePosition();
}

function submit(): void {
  const message = text.value.trim();
  if (!message || busy.value) return;
  stopSpeaking();
  appendUserTurn(message);
  voiceNotice.value = "";
  emit("submit", message);
  text.value = "";
  openPanel();
}

function handleTextKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    submit();
  }
}

function resizeComposer(): void {
  void nextTick(() => {
    const textarea = barTextareaRef.value;
    if (!textarea) return;
    const maxHeight = 78;
    textarea.style.height = "40px";
    const nextHeight = Math.min(maxHeight, Math.max(40, textarea.scrollHeight));
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  });
}

function submitSuggested(message: string): void {
  if (!message || busy.value) return;
  stopSpeaking();
  appendUserTurn(message);
  voiceNotice.value = "";
  emit("submit", message);
  openPanel();
}

defineExpose({ open: openFromExternal });

function cardText(card: AgentCard, key: string, fallback = "待确认"): string {
  const value = card.data?.[key];
  if (Array.isArray(value)) return value.filter(Boolean).join("、") || fallback;
  return value == null || String(value).trim() === "" ? fallback : String(value);
}

function cardTextList(card: AgentCard, key: string): string[] {
  const value = card.data?.[key];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  return value == null || String(value).trim() === "" ? [] : [String(value)];
}

/** 确认发送草稿中已经展示的默认途经点；路线引擎的补点范围也由卡片里的候选名单约束。 */
function confirmDraftMessage(card: AgentCard): string {
  const must = cardTextList(card, "mustVisit");
  const optional = cardTextList(card, "optionalVisit");
  let message = "确认并生成路线：" + must.join("、");
  if (optional.length) message += "；顺路可选：" + optional.join("、") + "（默认不加）";
  return message;
}

function cancelActiveTurn(): void {
  if (!busy.value) return;
  stopSpeaking();
  if (recording.value && mediaRecorder?.state === "recording") {
    mediaRecorder.stop();
  }
  const latest = turns.value[turns.value.length - 1];
  if (latest && !latest.answer) {
    latest.answer = "已中断这次回答和页面操作。你可以直接重新提问。";
  } else if (latest && latest.commandProgress.some((item) => item.status === "running" || item.status === "waiting")) {
    latest.commandProgress = [
      ...latest.commandProgress,
      {
        commandId: `cancel-${latest.id}`,
        commandType: "cancel",
        status: "cancelled",
        summary: "已中断本轮操作"
      }
    ];
  }
  emit("cancel");
  scrollToBottom("smooth");
}

async function toggleRecording(): Promise<void> {
  if (recording.value) {
    mediaRecorder?.stop();
    return;
  }
  voiceNotice.value = "";
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    chunks = [];
    mediaRecorder = preferredAudioType
      ? new MediaRecorder(stream, { mimeType: preferredAudioType })
      : new MediaRecorder(stream);
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    mediaRecorder.onstop = () => {
      recording.value = false;
      stopLiveRecognition();
      stopStream();
      void submitAudio();
    };
    recording.value = true;
    liveTranscript.value = "";
    liveRecognitionSupportedThisTurn = startLiveRecognition();
    mediaRecorder.start();
    openPanel();
  } catch {
    voiceNotice.value = "无法访问麦克风，请确认浏览器权限和 HTTPS/localhost 环境。";
  }
}

async function submitAudio(): Promise<void> {
  if (!chunks.length) return;
  const browserTranscript = liveTranscript.value.trim() || text.value.trim();
  if (liveRecognitionSupportedThisTurn && browserTranscript) {
    sendRecognizedTranscript(browserTranscript);
    liveRecognitionSupportedThisTurn = false;
    return;
  }
  voiceBusy.value = true;
  try {
    const audio = new Blob(chunks, { type: chunks[0]?.type || preferredAudioType || "audio/webm" });
    const result = await recognizeSpeech(audio);
    const transcript = result.transcript || browserTranscript;
    if (!transcript) {
      voiceNotice.value = String(result.meta?.needs || result.meta?.message || "语音识别暂未就绪，请先用文字输入。");
      return;
    }
    sendRecognizedTranscript(transcript);
  } catch (error) {
    voiceNotice.value = error instanceof Error ? error.message : "语音识别失败。";
  } finally {
    voiceBusy.value = false;
    liveRecognitionSupportedThisTurn = false;
  }
}

/** 播报指定文本（供自动播报与"喇叭"按钮复用） */
function setTurnNotice(turn: AgentTurn, notice: string, copied = false): void {
  window.clearTimeout(turnNoticeTimers.get(turn.id));
  turn.actionNotice = notice;
  turn.copied = copied;
  const timer = window.setTimeout(() => {
    if (turn.actionNotice === notice) turn.actionNotice = "";
    if (copied) turn.copied = false;
    turnNoticeTimers.delete(turn.id);
  }, 1800);
  turnNoticeTimers.set(turn.id, timer);
}

function stripSpeechText(value: string): string {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/[*_`>#-]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function extractInterimSpeechIntro(answer: string): string {
  const text = stripSpeechText(answer);
  if (!/^(好的|好[，,。！!]|没问题|收到)/.test(text)) return "";
  const match = text.match(/^.+?[。！？!?](?=\s|$)/);
  return (match?.[0] || text).trim();
}

function stopCurrentAudio(): void {
  const audio = currentAudio;
  currentAudio = null;
  speakingTurnId.value = null;
  if (audio) {
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    audio.currentTime = 0;
  }
  currentSpeechResolve?.();
  currentSpeechResolve = null;
}

function clearSpeechQueue(): void {
  speechQueueToken += 1;
  speechQueue = Promise.resolve();
  stopCurrentAudio();
}

async function playPreparedAudio(result: TtsResponse, options: { turnId?: number } = {}): Promise<void> {
  if (!result.audioBase64) {
    voiceNotice.value = String(result.meta?.needs || result.meta?.message || "语音播报暂未就绪。");
    return;
  }
  currentAudio = playBase64Audio(result.audioBase64, result.mimeType);
  speakingTurnId.value = options.turnId ?? null;
  if (currentAudio) {
    await new Promise<void>((resolve) => {
      currentSpeechResolve = resolve;
      currentAudio!.onended = () => {
        if (currentAudio) {
          currentAudio = null;
          speakingTurnId.value = null;
        }
        currentSpeechResolve = null;
        resolve();
      };
      currentAudio!.onerror = () => {
        if (currentAudio) {
          currentAudio = null;
          speakingTurnId.value = null;
        }
        currentSpeechResolve = null;
        resolve();
      };
    });
  }
}

async function playSpeechNow(answer: string, options: { force?: boolean; turnId?: number } = {}): Promise<void> {
  if (!answer || (!options.force && !speechEnabled.value)) return;
  const runToken = speechQueueToken;
  voiceBusy.value = true;
  try {
    const result = await synthesizeSpeech(answer);
    if (runToken !== speechQueueToken) return;
    await playPreparedAudio(result, options);
  } catch (error) {
    voiceNotice.value = error instanceof Error ? error.message : "语音播报失败。";
  } finally {
    voiceBusy.value = false;
  }
}

async function speakText(answer: string, options: { force?: boolean; turnId?: number } = {}): Promise<void> {
  clearSpeechQueue();
  await playSpeechNow(answer, options);
}

function enqueueAutoSpeech(answer: string, turnId?: number): void {
  const text = stripSpeechText(answer);
  if (!text || !speechEnabled.value) return;
  const token = speechQueueToken;
  voiceBusy.value = true;
  // 预取：入队即开始合成，队列只约束播放顺序；
  // 上一句播完时下一句音频已就绪，立即衔接、句间无停顿
  const synth = synthesizeSpeech(text).catch((error: unknown): TtsResponse => ({
    audioBase64: "",
    mimeType: "",
    provider: "",
    meta: { message: error instanceof Error ? error.message : "语音播报失败。" }
  }));
  speechQueue = speechQueue
    .then(async () => {
      if (token !== speechQueueToken || !speechEnabled.value) {
        if (token === speechQueueToken) voiceBusy.value = false;
        return;
      }
      const result = await synth;
      if (token !== speechQueueToken) return;
      try {
        await playPreparedAudio(result, { turnId });
      } finally {
        voiceBusy.value = false;
      }
    })
    .catch(() => {});
}

/** 播报指定轮次的回答（喇叭按钮） */
function speakTurnAnswer(turn: AgentTurn): void {
  if (speakingTurnId.value === turn.id && currentAudio) {
    stopSpeaking();
    setTurnNotice(turn, "已停止播报");
    return;
  }
  setTurnNotice(turn, "正在播报");
  void speakText(turn.answer || "", { force: true, turnId: turn.id });
}

/** 复制指定轮次的回答（复制按钮） */
async function copyTurnAnswer(turn: AgentTurn): Promise<void> {
  const text = turn.answer || "";
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
  setTurnNotice(turn, "已复制", true);
}

function toggleSpeech(): void {
  speechEnabled.value = !speechEnabled.value;
  localStorage.setItem(SPEECH_KEY, speechEnabled.value ? "1" : "0");
  // 打开播报时不自动播报上一轮已完成的回答；从下一轮起新回答到达后自动播报
}

function stopSpeaking(): void {
  clearSpeechQueue();
  voiceBusy.value = false;
}

function sendRecognizedTranscript(transcript: string): void {
  text.value = transcript;
  liveTranscript.value = transcript;
  openPanel();
  stopSpeaking();
  appendUserTurn(transcript);
  emit("submit", transcript);
  text.value = "";
}

function appendUserTurn(question: string): void {
  turns.value.forEach((turn) => {
    turn.suggestedActions = [];
  });
  turns.value.push({
    id: ++turnIdSeed,
    question,
    answer: "",
    cards: [],
    toolSteps: [],
    commandProgress: [],
    v4ProcessSteps: [],
    suggestedActions: [],
    detailsOpen: true
  });
  scrollToBottom("smooth");
}

function appendAssistantResponse(response: AgentV2ChatResponse): void {
  // V4 refreshes the same response object when final follow-up questions or
  // the final answer arrive. Match by backend turnId first, otherwise a
  // reactive response update would append a duplicate assistant message.
  const existing = response.turnId
    ? turns.value.find((item) => item.backendTurnId === response.turnId)
    : undefined;
  const turn = existing || [...turns.value].reverse().find((item) => !item.answer);
  const target = turn || {
    id: ++turnIdSeed,
    question: "",
    answer: "",
    cards: [],
    toolSteps: [],
    commandProgress: [],
    v4ProcessSteps: [],
    suggestedActions: [],
    detailsOpen: true
  };
  target.answer = response.answer;
  target.backendTurnId = response.turnId;
  target.cards = response.cards || [];
  target.toolSteps = response.debugToolSteps || [];
  // A streamed V4 graph has already populated the real node events. Later
  // response refreshes (for example follow-up questions or the final answer)
  // must not replace those events with the response's compact fallback list.
  if (!target.v4ProcessSteps.length) {
    target.v4ProcessSteps = response.v4ProcessSteps || [];
  }
  target.suggestedActions = response.suggestedActions || [];
  target.guidance = response.guidance || null;
  if (!turn) turns.value.push(target);
  scrollToBottom("smooth");
}

function buildWelcomeGuidance(context: AgentMapContext | null): AgentGuidance {
  const buildingName = context?.lastMentionedBuildingName || "这栋建筑";
  if (context?.selectedBuildingId) {
    return {
      mode: "ONBOARDING",
      message: `你现在正在看${buildingName}。我可以从它继续带你探索。`,
      firstWelcome: true,
      suggestions: [
        { id: "building-story", label: "讲讲它的故事", prompt: `讲讲${buildingName}的故事`, icon: "story" },
        { id: "building-relations", label: "人物和事件", prompt: `${buildingName}和哪些人物事件有关`, icon: "relation" },
        { id: "building-route", label: "加入游览路线", prompt: `把${buildingName}加入游览路线`, icon: "route" }
      ]
    };
  }
  if (context?.selectedMemoryGridId) {
    return {
      mode: "ONBOARDING",
      message: "你正在查看记忆网格。我可以解释它为什么这样判断，并找出其中的代表建筑。",
      firstWelcome: true,
      suggestions: [
        { id: "grid-special", label: "这个网格有什么特别", prompt: "这个网格有什么特别", icon: "grid" },
        { id: "grid-buildings", label: "这里有哪些代表建筑", prompt: "这里有哪些代表建筑", icon: "building" },
        { id: "grid-discover", label: "最值得关注的地方", prompt: "带我看看最值得关注的地方", icon: "discover" }
      ]
    };
  }
  if (context?.activeRouteId) {
    return {
      mode: "ONBOARDING",
      message: "你已经有一条游览路线了。我可以介绍下一站，或继续发现沿线的城市记忆。",
      firstWelcome: true,
      suggestions: [
        { id: "route-story", label: "讲讲这条路线", prompt: "讲讲这条路线", icon: "route" },
        { id: "route-next", label: "介绍下一站", prompt: "介绍下一站", icon: "next" },
        { id: "route-replan", label: "重新规划", prompt: "重新规划一条文化路线", icon: "refresh" }
      ]
    };
  }
  const onMap = Boolean(context?.mapCenter || context?.visibleBuildingIds?.length || context?.routePath);
  return {
    mode: "ONBOARDING",
    message: onMap
      ? "从一处具体的城市记忆开始吧：我可以为你定位建筑、讲人物与事件，或按时长设计一条文化路线。"
      : "今天想从哪里认识武汉？你不需要记住准确名称，描述一个地方、人物或故事也可以。",
    firstWelcome: true,
    suggestions: onMap
      ? [
          { id: "map-locate-xiananfang", label: "定位咸安坊", prompt: "带我定位咸安坊", icon: "building" },
          { id: "map-wuhan-liberation", label: "武汉解放第一声电报", prompt: "武汉解放的第一声电报从哪发出", icon: "history" },
          { id: "map-route-hankou", label: "两小时汉口建筑漫步", prompt: "帮我规划一条两小时的汉口近代建筑路线", icon: "route" }
        ]
      : [
          { id: "discover-building", label: "找建筑", prompt: "带我看看江汉关", icon: "building" },
          { id: "discover-history", label: "听故事", prompt: "汉口为什么有这么多老银行？", icon: "history" },
          { id: "discover-route", label: "规划路线", prompt: "帮我安排一条两小时的近代建筑路线", icon: "route" }
        ]
  };
}

function guidanceSuggestions(turn: AgentTurn): AgentGuidanceSuggestion[] {
  return turn.guidance?.suggestions?.length
    ? turn.guidance.suggestions
    : turn.suggestedActions.map((prompt, index) => ({ id: `legacy-${index}`, label: prompt, prompt }));
}

function latestTurn(): AgentTurn | null {
  return turns.value[turns.value.length - 1] || null;
}

function scrollToBottom(behavior: ScrollBehavior = "auto"): void {
  void nextTick(() => {
    const body = bodyRef.value;
    if (!body) return;
    body.scrollTo({ top: body.scrollHeight, behavior });
    bottomRef.value?.scrollIntoView({ block: "end", behavior });
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderInlineMarkdown(value: string): string {
  return value
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
}

function renderMarkdown(value: string): string {
  const lines = escapeHtml(value || "").replace(/\r\n/g, "\n").split("\n");
  const html: string[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let ordered = false;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    html.push(`<p>${renderInlineMarkdown(paragraph.join("<br>"))}</p>`);
    paragraph = [];
  };
  const flushList = () => {
    if (!list.length) return;
    html.push(`${ordered ? "<ol>" : "<ul>"}${list.join("")}${ordered ? "</ol>" : "</ul>"}`);
    list = [];
    ordered = false;
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      html.push(`<strong>${renderInlineMarkdown(heading[2])}</strong>`);
      continue;
    }
    const unordered = /^[-*]\s+(.+)$/.exec(line);
    const numbered = /^\d+\.\s+(.+)$/.exec(line);
    if (unordered || numbered) {
      flushParagraph();
      const shouldOrder = Boolean(numbered);
      if (list.length && ordered !== shouldOrder) flushList();
      ordered = shouldOrder;
      list.push(`<li>${renderInlineMarkdown((unordered || numbered)?.[1] || "")}</li>`);
      continue;
    }
    flushParagraph();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return html.join("");
}

function startLiveRecognition(): boolean {
  const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!SpeechRecognitionCtor) return false;
  try {
    liveRecognition = new SpeechRecognitionCtor();
    liveRecognition.lang = "zh-CN";
    liveRecognition.continuous = true;
    liveRecognition.interimResults = true;
    liveRecognition.onresult = (event: any) => {
      let combined = "";
      for (let index = 0; index < event.results.length; index += 1) {
        combined += event.results[index][0]?.transcript || "";
      }
      liveTranscript.value = combined.trim();
      if (liveTranscript.value) text.value = liveTranscript.value;
    };
    liveRecognition.onerror = () => undefined;
    liveRecognition.start();
    return true;
  } catch {
    liveRecognition = null;
    return false;
  }
}

function stopLiveRecognition(): void {
  try {
    liveRecognition?.stop?.();
  } catch {
    // Browser recognition may already be stopped.
  }
  liveRecognition = null;
}

function stopStream(): void {
  stream?.getTracks().forEach((track) => track.stop());
  stream = null;
}

watch(
  () => props.response,
  (response) => {
    if (response?.answer && response !== lastRenderedResponse) {
      lastRenderedResponse = response;
      appendAssistantResponse(response);
      if (speechEnabled.value) {
        if (text.value === liveTranscript.value) text.value = "";
        enqueueAutoSpeech(extractInterimSpeechIntro(response.answer), latestTurn()?.id);
      }
    }
  }
);

watch(text, resizeComposer);

watch(
  () => props.commandProgress || [],
  (progress) => {
    const latest = latestTurn();
    if (!latest) return;
    latest.commandProgress = [...progress];
    scrollToBottom("smooth");
  },
  { deep: true }
);

watch(
  () => props.v4ProcessSteps || [],
  (steps) => {
    if (!steps.length) return;
    const latest = latestTurn();
    if (!latest) return;
    latest.v4ProcessSteps = [...steps];
    if (!latest.finalized) latest.detailsOpen = true;
    scrollToBottom("smooth");
  },
  { deep: true }
);

// 等待回复期间：后端把执行步骤逐条推送到 liveSteps，这里追加到本轮执行过程，
// 让等待不再干着急。最终结果到达后由 response 的 debugToolSteps 整体替换，不会重复。
watch(
  () => props.liveSteps || [],
  (steps) => {
    const latest = latestTurn();
    if (!latest) return;
    const seen = new Set(latest.toolSteps.map((step) => `${step.tool}|${step.status}|${step.summary}`));
    const fresh = steps.filter((step) => {
      const key = `${step.tool}|${step.status}|${step.summary}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!fresh.length) return;
    latest.toolSteps = [...latest.toolSteps, ...fresh];
    latest.detailsOpen = true;
    scrollToBottom("smooth");
  },
  { deep: true }
);

watch(
  () => props.loading,
  (loading) => {
    if (!loading && !recording.value) {
      liveTranscript.value = "";
    }
    if (loading) {
      scrollToBottom("smooth");
    }
  }
);

watch(panelOpen, (value) => {
  if (value) {
    scrollToBottom("auto");
  }
});

// 命令执行完成后，用最终回答替换本轮过渡回答，执行过程默认收起（可点开）
watch(
  () => props.finalAnswer,
  (committed) => {
    if (!committed?.answer) return;
    const target = turns.value.find((turn) => turn.backendTurnId === committed.turnId);
    if (target) {
      target.answer = committed.answer;
      target.finalized = true;
      target.detailsOpen = false;
      if (speechEnabled.value) {
        enqueueAutoSpeech(committed.answer, target.id);
      }
      scrollToBottom("smooth");
    }
  }
);

watch(
  () => props.errorMessage,
  (message) => {
    if (!message) return;
    const turn = [...turns.value].reverse().find((item) => !item.answer);
    if (turn) turn.answer = message;
    scrollToBottom("smooth");
  }
);

onMounted(() => {
  initializePosition();
  speechEnabled.value = localStorage.getItem(SPEECH_KEY) !== "0";
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  if (mediaRecorder?.state === "recording") mediaRecorder.stop();
  stopLiveRecognition();
  stopStream();
  stopSpeaking();
  turnNoticeTimers.forEach((timer) => window.clearTimeout(timer));
  turnNoticeTimers.clear();
  window.removeEventListener("pointermove", moveDrag);
  window.removeEventListener("resize", handleResize);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="loading || props.executing" class="map-agent-aura" aria-hidden="true">
      <span />
      <span />
      <b />
    </div>
  </Teleport>

  <!-- 可拖拽悬浮球（江小忆 · 江豚） -->
  <!-- 收起态：侧边可拖拽江豚小球（点击展开；拖拽可移动） -->
  <div
    v-if="!open"
    class="map-agent-orb-wrap"
    :style="orbStyle"
    aria-label="江城记忆智能体"
  >
    <button
      class="map-agent-orb"
      type="button"
      :class="{ 'is-busy': busy, 'is-dragging': dragging }"
      :aria-expanded="open"
      aria-label="打开江城记忆智能体"
      data-tooltip="江小忆 · 长江江豚"
      @pointerdown="startDrag"
      @click="handleOrbClick"
    >    <span class="orb-dome" aria-hidden="true">
        <i class="orb-star s1" />
        <i class="orb-star s2" />
        <i class="orb-star s3" />
        <svg class="porpoise" viewBox="0 0 64 64" focusable="false">
          <defs>
            <linearGradient id="porpoiseBody" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#c4dff3" />
              <stop offset=".48" stop-color="#ecf6ff" />
              <stop offset="1" stop-color="#ffffff" />
            </linearGradient>
          </defs>
          <g class="porpoise-spout">
            <circle class="drop d1" cx="32" cy="6.4" r="1.7" />
            <circle class="drop d2" cx="27.4" cy="8.6" r="1.15" />
            <circle class="drop d3" cx="36.6" cy="8.6" r="1.15" />
          </g>
          <path class="porpoise-fin is-left" d="M16.5 37.5 C10 39 6.5 44.5 8.5 48.5 C13 48 16.5 44.5 18.5 41 Z" />
          <path class="porpoise-fin is-right" d="M47.5 37.5 C54 39 57.5 44.5 55.5 48.5 C51 48 47.5 44.5 45.5 41 Z" />
          <path
            class="porpoise-body"
            d="M32 11.5 C21.5 11.5 14.5 20.5 14.5 31 C14.5 44 22 54.5 32 54.5 C42 54.5 49.5 44 49.5 31 C49.5 20.5 42.5 11.5 32 11.5 Z"
          />
          <ellipse class="porpoise-belly" cx="32" cy="43.5" rx="10.5" ry="8.5" />
          <g class="porpoise-eye is-left">
            <circle class="eye-ball" cx="24.5" cy="29.5" r="4.4" />
            <circle class="eye-glint" cx="26.2" cy="27.8" r="1.5" />
          </g>
          <g class="porpoise-eye is-right">
            <circle class="eye-ball" cx="39.5" cy="29.5" r="4.4" />
            <circle class="eye-glint" cx="41.2" cy="27.8" r="1.5" />
          </g>
          <circle class="porpoise-blush" cx="20" cy="36.2" r="2.7" />
          <circle class="porpoise-blush" cx="44" cy="36.2" r="2.7" />
          <path class="porpoise-smile" d="M25.5 38.5 Q32 43.5 38.5 38.5" />
          <path class="porpoise-blowhole" d="M29 13.8 Q32 12.2 35 13.8" />
        </svg>
      </span>
      <span class="orbit-tilt" aria-hidden="true">
        <span class="orbit-ring" />
        <span class="orbit-spark a"><i /></span>
        <span class="orbit-spark b"><i /></span>
      </span>
    </button>
  </div>

  <div
    v-else
    class="map-agent-dock"
    :class="{ 'is-busy': busy, 'is-recording': recording }"
    aria-label="江城记忆智能体"
  >

    <section v-if="panelOpen" class="map-agent-panel" aria-label="江小忆对话面板">
        <header>
          <div class="panel-title">
            <small>Jiangcheng Memory</small>
            <strong>江小忆</strong>
          </div>
          <div class="panel-actions">
            <button class="map-agent-new-chat" type="button" aria-label="新对话" data-tooltip="新对话" @click="startNewConversation">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
                <path d="M15 5h6M18 2v6" />
                <path d="M8 12h7M8 16h5" />
              </svg>
            </button>
            <button
              class="map-agent-speech-toggle"
              type="button"
              :class="{ 'is-off': !speechEnabled }"
              :aria-pressed="speechEnabled"
              :data-tooltip="speechEnabled ? '关闭自动播报' : '开启自动播报'"
              :aria-label="speechEnabled ? '关闭语音播报' : '开启语音播报'"
              @click="toggleSpeech"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none" />
                <template v-if="speechEnabled">
                  <path d="M15.5 9a4.2 4.2 0 0 1 0 6" />
                  <path d="M18 6.5a8 8 0 0 1 0 11" />
                </template>
                <template v-else>
                  <path d="m16 9 6 6" />
                  <path d="m22 9-6 6" />
                </template>
              </svg>
            </button>
            <button class="panel-close" type="button" aria-label="收起回答面板" data-tooltip="收起回答面板" @click="closePanel">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        </header>

        <div ref="bodyRef" class="map-agent-body">
          <section v-if="!turns.length && !loading" class="map-agent-welcome">
            <p class="map-agent-placeholder">
              <span>{{ welcomeGuidance.message }}</span>
              <small>也可以直接告诉我你想去哪儿、想了解谁，或者描述一个记不清名字的地方。</small>
            </p>
            <div class="map-agent-suggestions is-welcome">
              <button
                v-for="item in welcomeGuidance.suggestions"
                :key="item.id"
                type="button"
                :disabled="busy"
                @click="submitSuggested(item.prompt)"
              >{{ item.label }}</button>
            </div>
          </section>
          <div v-if="turns.length" class="map-agent-thread">
            <article v-for="turn in turns" :key="turn.id" class="map-agent-turn">
              <p v-if="turn.question" class="map-agent-user-line">
                <small>你说</small>
                <span>{{ turn.question }}</span>
              </p>

              <button
                v-if="turn.v4ProcessSteps.length || turn.toolSteps.length || turn.commandProgress.length"
                type="button"
                class="map-agent-details-toggle"
                :class="{ 'is-open': turn.detailsOpen }"
                @click="toggleDetails(turn)"
              >
                <i />
                <span>{{ turn.v4ProcessSteps.length ? 'V4 执行过程' : '执行过程' }} · {{ turn.v4ProcessSteps.length + turn.toolSteps.length + turn.commandProgress.length }} 条</span>
              </button>
              <template v-if="turn.detailsOpen">
                <ol v-if="turn.v4ProcessSteps.length" class="map-agent-v4-process">
                  <li
                    v-for="step in turn.v4ProcessSteps"
                    :key="`${turn.id}-v4-${step.id}`"
                    :class="`is-${String(step.status).toLowerCase()}`"
                  >
                    <i />
                    <span><strong>{{ step.title }}</strong><small v-if="step.detail">{{ step.detail }}</small></span>
                  </li>
                </ol>
                <ol v-if="turn.toolSteps.length" class="map-agent-steps">
                  <li v-for="step in turn.toolSteps" :key="`${turn.id}-${step.tool}-${step.summary}`">
                    <i :class="`is-${step.status}`" />
                    <span>{{ step.summary }}</span>
                  </li>
                </ol>
                <ol
                  v-if="turn.commandProgress.length"
                  class="map-agent-command-progress"
                >
                  <li
                    v-for="item in turn.commandProgress.slice(0, 8)"
                    :key="item.commandId"
                    :class="`is-${item.status}`"
                  >
                    <i />
                    <span>{{ item.summary || item.commandType }}</span>
                  </li>
                </ol>
              </template>

              <div
                v-if="!turn.answer && loading && interimAnswer && turn.id === latestTurn()?.id"
                class="map-agent-interim-answer"
              >{{ interimAnswer }}</div>

              <template v-if="turn.answer">
                <div class="map-agent-answer" :class="{ 'is-final': turn.finalized }" v-html="renderMarkdown(turn.answer)" />
                <div class="map-agent-turn-actions">
                  <button
                    type="button"
                    class="act-btn act-speak"
                    :class="{ 'is-speaking': speakingTurnId === turn.id }"
                    :data-tooltip="speakingTurnId === turn.id ? '停止播报' : '播报该回答'"
                    :aria-label="speakingTurnId === turn.id ? '停止播报该回答' : '播报该回答'"
                    @click="speakTurnAnswer(turn)"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none" />
                      <path d="M15.5 9a4.2 4.2 0 0 1 0 6" />
                      <path d="M18 6.5a8 8 0 0 1 0 11" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="act-btn act-copy"
                    :class="{ 'is-copied': turn.copied }"
                    :data-tooltip="turn.copied ? '已复制' : '复制该回答'"
                    aria-label="复制该回答"
                    @click="copyTurnAnswer(turn)"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  </button>
                  <span v-if="turn.actionNotice" class="map-agent-action-note">{{ turn.actionNotice }}</span>
                </div>

                <div v-if="turn.cards.length" class="map-agent-cards" role="list" aria-label="相关卡片">
                  <template v-for="card in turn.cards" :key="`${turn.id}-${card.type}-${card.id}`">
                    <section v-if="card.type === 'tour_intent_draft'" class="map-agent-intent-card" role="listitem">
                      <header><span><small>AI 已提取</small><strong>{{ card.title }}</strong></span><b>待确认</b></header>
                      <div class="intent-card-grid"><span><small>日期 / 出发</small><b>{{ cardText(card, 'date') }} · {{ cardText(card, 'startTime') }}</b></span><span><small>总时长</small><b>{{ cardText(card, 'durationMinutes') }} 分钟</b></span><span><small>起点</small><b>{{ cardText(card, 'start') }}</b></span><span><small>终点</small><b>{{ cardText(card, 'end') }}</b></span></div>
                        <dl><div><dt>建议途经点</dt><dd>{{ cardText(card, 'mustVisit') }}</dd></div><div><dt>路线候选范围</dt><dd>{{ cardText(card, 'candidateNames', cardText(card, 'platformMatches')) }}</dd></div><div><dt>顺路可选</dt><dd>{{ cardText(card, 'optionalVisit', '无') }}</dd></div><div><dt>待确认加入</dt><dd>{{ cardText(card, 'streetCandidates', '无') }}</dd></div><div><dt>加入理由</dt><dd>{{ cardText(card, 'candidateExplanations', '无') }}</dd></div><div><dt>距离 / 时间</dt><dd>{{ cardText(card, 'routeEstimate', '待 SuperMap 计算') }}</dd></div><div><dt>注意事项</dt><dd>{{ cardText(card, 'routeNotes', '无') }}</dd></div><div><dt>偏好</dt><dd>{{ cardText(card, 'preferences') }}</dd></div><div><dt>回避</dt><dd>{{ cardText(card, 'avoid') }}</dd></div><div><dt>候选来源</dt><dd>{{ cardText(card, 'candidateSources') }}</dd></div></dl>
                      <p>确认后，SuperMap 只会在已列出的路线候选范围内补充站点并计算路网，不会再从全图另选地点。</p>
                      <button type="button" :disabled="busy" @click="submitSuggested(confirmDraftMessage(card))">确认并生成路线</button>
                    </section>
                    <button v-else type="button" class="map-agent-chip" role="listitem" :data-tooltip="card.body || card.title"><span class="chip-main"><strong>{{ card.title }}</strong><small v-if="card.subtitle">{{ card.subtitle }}</small></span></button>
                  </template>
                </div>

                <div v-if="guidanceSuggestions(turn).length && turn.id === latestSuggestionTurnId && !busy" class="map-agent-suggestions">
                  <button
                    v-for="item in guidanceSuggestions(turn).slice(0, 4)"
                    :key="`${turn.id}-${item.id}`"
                    type="button"
                    @click="submitSuggested(item.prompt)"
                  >
                    {{ item.label }}
                  </button>
                </div>
              </template>
            </article>
          </div>
          <p v-if="recording" class="map-agent-live-line">
            <small>正在听</small>
            <span>{{ liveTranscript || "请继续说，我在听。" }}</span>
          </p>
          <div v-if="loading" class="map-agent-working">
            <span />
            <b>正在查找相关资料</b>
          </div>
          <p v-if="errorMessage" class="map-agent-error">{{ errorMessage }}</p>
          <p v-if="voiceNotice" class="map-agent-voice-note">{{ voiceNotice }}</p>
          <div ref="bottomRef" class="map-agent-bottom-anchor" aria-hidden="true" />
        </div>
      </section>

    <footer class="map-agent-bar">
      <button
        class="bar-avatar"
        type="button"
        :aria-expanded="panelOpen"
        aria-label="收起为侧边江豚"
        data-tooltip="收起为侧边图标"
        @click="closeDock"
      >
        <svg class="porpoise" viewBox="0 0 64 64" focusable="false" aria-hidden="true">
          <defs>
            <linearGradient id="porpoiseBody" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#c4dff3" />
              <stop offset=".48" stop-color="#ecf6ff" />
              <stop offset="1" stop-color="#ffffff" />
            </linearGradient>
          </defs>
          <g class="porpoise-spout">
            <circle class="drop d1" cx="32" cy="6.4" r="1.7" />
            <circle class="drop d2" cx="27.4" cy="8.6" r="1.15" />
            <circle class="drop d3" cx="36.6" cy="8.6" r="1.15" />
          </g>
          <path class="porpoise-fin is-left" d="M16.5 37.5 C10 39 6.5 44.5 8.5 48.5 C13 48 16.5 44.5 18.5 41 Z" />
          <path class="porpoise-fin is-right" d="M47.5 37.5 C54 39 57.5 44.5 55.5 48.5 C51 48 47.5 44.5 45.5 41 Z" />
          <path
            class="porpoise-body"
            d="M32 11.5 C21.5 11.5 14.5 20.5 14.5 31 C14.5 44 22 54.5 32 54.5 C42 54.5 49.5 44 49.5 31 C49.5 20.5 42.5 11.5 32 11.5 Z"
          />
          <ellipse class="porpoise-belly" cx="32" cy="43.5" rx="10.5" ry="8.5" />
          <g class="porpoise-eye is-left">
            <circle class="eye-ball" cx="24.5" cy="29.5" r="4.4" />
            <circle class="eye-glint" cx="26.2" cy="27.8" r="1.5" />
          </g>
          <g class="porpoise-eye is-right">
            <circle class="eye-ball" cx="39.5" cy="29.5" r="4.4" />
            <circle class="eye-glint" cx="41.2" cy="27.8" r="1.5" />
          </g>
          <circle class="porpoise-blush" cx="20" cy="36.2" r="2.7" />
          <circle class="porpoise-blush" cx="44" cy="36.2" r="2.7" />
          <path class="porpoise-smile" d="M25.5 38.5 Q32 43.5 38.5 38.5" />
          <path class="porpoise-blowhole" d="M29 13.8 Q32 12.2 35 13.8" />
        </svg>
      </button>
      <span class="bar-name">江小忆</span>
      <textarea
        ref="barTextareaRef"
        v-model="text"
        rows="1"
        maxlength="6000"
        placeholder="说出或输入你想做的"
        @input="resizeComposer"
        @keydown="handleTextKeydown"
        @focus="openPanel"
      />
      <button
        class="bar-btn is-mic"
        type="button"
        :class="{ 'is-recording': recording }"
        :disabled="busy || voiceBusy"
        :aria-label="recording ? '停止录音' : '语音输入'"
        :data-tooltip="recording ? '停止录音' : '语音输入'"
        @click="toggleRecording"
      >
        <svg v-if="!recording" viewBox="0 0 24 24" width="17" height="17" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="9" y="2.5" width="6" height="11" rx="3" />
          <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
          <path d="M12 17.5V21" />
        </svg>
        <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
          <rect x="7" y="7" width="10" height="10" rx="2" />
        </svg>
      </button>
      <button v-if="busy" type="button" class="bar-btn is-stop" data-tooltip="中断本次回答" @click="cancelActiveTurn">中断</button>
      <button v-else type="button" class="bar-btn is-send" :disabled="!text.trim()" aria-label="发送" data-tooltip="发送" @click="submit">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M3.4 11.3 21 3.2l-8.1 17.4-2.3-7.2-7.2-2.1z" />
        </svg>
      </button>
    </footer>
  </div>
</template>
